import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/api';

const formatAmount = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const STATUS_STYLES = {
  ACTIVE: 'bg-emerald-100 text-emerald-800',
  EXPIRING_TODAY: 'bg-amber-100 text-amber-900',
  GRACE: 'bg-orange-100 text-orange-800',
  EXPIRED: 'bg-red-100 text-red-800',
};

const CARDS = [
  {
    id: 'earningsThisMonth',
    title: 'Earnings this month',
    subtitle: (s) => s.monthLabel || 'Current month',
    valueKey: 'earningsThisMonth',
    detailKey: 'earningsThisMonth',
    gradient: 'from-emerald-500 to-teal-600',
    icon: '₹',
    filter: null,
    description:
      'Subscription amounts recorded with a last payment date in the current calendar month.',
  },
  {
    id: 'paymentDue',
    title: 'Payment due',
    subtitle: () => 'Renewal needed',
    valueKey: 'paymentDueTotal',
    detailKey: 'paymentDue',
    gradient: 'from-amber-500 to-orange-600',
    icon: '!',
    filter: 'expiring',
    description:
      'Shops that are expiring within 7 days, expiring today, in grace period, or fully expired.',
  },
  {
    id: 'totalShops',
    title: 'Total shops',
    subtitle: () => 'On PocketBillingAI',
    valueKey: 'totalShops',
    detailKey: 'totalShops',
    gradient: 'from-indigo-500 to-violet-600',
    icon: '◆',
    filter: 'all',
    description: 'All registered PocketBillingAI shops.',
  },
  {
    id: 'inactiveShops',
    title: 'Inactive shops',
    subtitle: () => 'Blocked or expired',
    valueKey: 'inactiveShops',
    detailKey: 'inactiveShops',
    gradient: 'from-stone-500 to-stone-700',
    icon: '○',
    filter: 'expired',
    description: 'Accounts turned off by admin or past grace period (cannot log in).',
  },
  {
    id: 'activeShops',
    title: 'Active shops',
    subtitle: () => 'Can use the app',
    valueKey: 'activeShops',
    detailKey: 'activeShops',
    gradient: 'from-sky-500 to-blue-600',
    icon: '✓',
    filter: 'active',
    description: 'Shops with an active subscription or grace access.',
  },
  {
    id: 'expiringSoon',
    title: 'Expiring soon',
    subtitle: () => 'Within 7 days',
    valueKey: 'expiringSoonCount',
    detailKey: 'expiringSoon',
    gradient: 'from-rose-500 to-pink-600',
    icon: '⏱',
    filter: 'expiring',
    description: 'Active shops whose subscription ends within the next 7 days.',
  },
];

function DetailModal({ open, onClose, card, rows, onViewInTable, formatAmount: fmt }) {
  if (!open || !card) return null;

  const isCountCard = card.valueKey === 'totalShops' || card.valueKey === 'inactiveShops' || card.valueKey === 'activeShops' || card.valueKey === 'expiringSoonCount';
  const totalAmt = rows.reduce((s, r) => s + (Number(r.subscriptionAmount) || 0), 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden">
        <div className={`px-6 py-5 bg-gradient-to-r ${card.gradient} text-white`}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-white/80 text-sm font-medium">{card.title}</p>
              <p className="text-2xl font-bold mt-1">
                {isCountCard ? rows.length : fmt(totalAmt)}
                {!isCountCard && rows.length > 0 && (
                  <span className="text-base font-normal text-white/90 ml-2">
                    · {rows.length} shop{rows.length !== 1 ? 's' : ''}
                  </span>
                )}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-white/90 hover:text-white text-2xl leading-none"
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <p className="text-sm text-white/85 mt-2">{card.description}</p>
        </div>

        <div className="flex-1 overflow-auto p-4">
          {rows.length === 0 ? (
            <p className="text-stone-500 text-center py-8">No shops in this group.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-stone-500 border-b border-stone-200">
                  <th className="pb-2 pr-3">Shop</th>
                  <th className="pb-2 pr-3">Owner</th>
                  <th className="pb-2 pr-3">Mobile</th>
                  <th className="pb-2 pr-3">Last payment</th>
                  <th className="pb-2 pr-3">Valid until</th>
                  <th className="pb-2 pr-3 text-right">Amount</th>
                  <th className="pb-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-stone-50">
                    <td className="py-2.5 pr-3 font-medium">{r.shopName}</td>
                    <td className="py-2.5 pr-3">{r.ownerName}</td>
                    <td className="py-2.5 pr-3 tabular-nums">{r.mobileNumber}</td>
                    <td className="py-2.5 pr-3 tabular-nums">{r.lastPaymentDate || '—'}</td>
                    <td className="py-2.5 pr-3 tabular-nums">{r.subscriptionEndDate || '—'}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums font-medium">
                      {fmt(r.subscriptionAmount)}
                    </td>
                    <td className="py-2.5">
                      <span
                        className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                          STATUS_STYLES[r.status] || 'bg-stone-100'
                        }`}
                      >
                        {r.status}
                        {!r.isActive ? ' · off' : ''}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="border-t border-stone-200 px-4 py-3 flex flex-wrap gap-2 justify-end bg-stone-50">
          {card.filter && onViewInTable && (
            <button
              type="button"
              onClick={() => {
                onViewInTable(card.filter);
                onClose();
              }}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
            >
              Show in table below
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-stone-300 text-sm font-medium hover:bg-white"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default function RestroLiteMetricsDashboard({ onFilterFromCard, refreshToken = 0 }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailCard, setDetailCard] = useState(null);

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(API_ENDPOINTS.RESTRO_LITE_STATS);
      setStats(res.data);
    } catch {
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats, refreshToken]);

  const openDetail = (card) => {
    if (!stats?.details) return;
    setDetailCard(card);
  };

  const detailRows = detailCard && stats?.details?.[detailCard.detailKey]
    ? stats.details[detailCard.detailKey]
    : [];

  if (loading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 animate-pulse">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="h-28 rounded-2xl bg-stone-200" />
        ))}
      </div>
    );
  }

  if (!stats) return null;

  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {CARDS.map((card) => {
          const value =
            card.valueKey === 'earningsThisMonth' || card.valueKey === 'paymentDueTotal'
              ? formatAmount(stats[card.valueKey])
              : stats[card.valueKey] ?? 0;
          const sub = typeof card.subtitle === 'function' ? card.subtitle(stats) : card.subtitle;

          return (
            <button
              key={card.id}
              type="button"
              onClick={() => openDetail(card)}
              className={`text-left rounded-2xl p-4 bg-gradient-to-br ${card.gradient} text-white shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-400`}
            >
              <div className="flex items-start justify-between gap-2">
                <span className="text-2xl opacity-90" aria-hidden>
                  {card.icon}
                </span>
                <span className="text-xs font-medium bg-white/20 rounded-full px-2 py-0.5">
                  View details
                </span>
              </div>
              <p className="text-sm font-medium text-white/90 mt-3">{card.title}</p>
              <p className="text-2xl sm:text-3xl font-bold mt-1 tracking-tight">{value}</p>
              <p className="text-xs text-white/80 mt-1">{sub}</p>
            </button>
          );
        })}
      </div>

      <DetailModal
        open={Boolean(detailCard)}
        card={detailCard}
        rows={detailRows}
        formatAmount={formatAmount}
        onClose={() => setDetailCard(null)}
        onViewInTable={onFilterFromCard}
      />
    </>
  );
}
